import React from 'react';
import { CircleCheckBig, Download, Printer, ThumbsUp } from 'lucide-react';
import type {
  AssistantPresentationChartSection,
  AssistantPresentationConfirmationSection,
  AssistantPresentationFormattedBodySection,
  AssistantPresentationMetricGridSection,
  AssistantPresentationModel,
  AssistantPresentationSummarySection,
  AssistantPresentationTableCell,
  AssistantPresentationTableSection,
} from '../types';
import styles from '../styles/AIAssistant.module.css';

const findSection = <TSection extends AssistantPresentationModel['sections'][number]['type']>(
  presentation: AssistantPresentationModel,
  type: TSection,
) => presentation.sections.find((section): section is Extract<AssistantPresentationModel['sections'][number], { type: TSection }> => (
  section.type === type
));

const parseMetricValue = (value: string): number | null => {
  const normalized = String(value ?? '').replace(/[^0-9.]+/g, '');
  if (!normalized) {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const renderBoldText = (text: string): React.ReactNode[] => text.split('**').map((part, index) => (
  index % 2 === 1 ? <strong key={`bold-${index}`}>{part}</strong> : <span key={`text-${index}`}>{part}</span>
));

const renderSummaryText = (value: string) => value
  .split(/\n{2,}/)
  .map((paragraph) => paragraph.trim())
  .filter((paragraph) => paragraph.length > 0)
  .map((paragraph, index) => (
    <p
      key={`summary-${index + 1}`}
      style={{
        margin: index === 0 ? 0 : '10px 0 0',
        fontSize: '12px',
        lineHeight: 1.5,
      }}
    >
      {paragraph.split('\n').map((line, lineIndex) => (
        <React.Fragment key={`line-${lineIndex + 1}`}>
          {renderBoldText(line)}
          {lineIndex < paragraph.split('\n').length - 1 ? <br /> : null}
        </React.Fragment>
      ))}
    </p>
  ));

const renderFooterText = (footerText: string) => {
  const normalized = footerText.replace(/\s+—\s+/g, ' — ').trim();
  if (!normalized.includes('View all')) {
    return normalized;
  }
  const prefix = normalized.replace(/\s*—\s*View all\s*$/i, '').trim();
  return (
    <>
      <span>{prefix}</span>
      <span>{' — '}</span>
      <button
        type="button"
        style={{
          border: 0,
          padding: 0,
          background: 'transparent',
          color: '#2697A2',
          font: 'inherit',
          cursor: 'pointer',
        }}
      >
        View all
      </button>
    </>
  );
};

const normalizeTableCell = (
  cell: string | AssistantPresentationTableCell | null | undefined,
): AssistantPresentationTableCell => {
  if (typeof cell === 'string') {
    return { text: cell };
  }
  return {
    text: String(cell?.text ?? ''),
    detail: cell?.detail ?? null,
    href: cell?.href ?? null,
    title: cell?.title ?? null,
    external: cell?.external ?? false,
  };
};

const renderTableCellContent = (
  cell: string | AssistantPresentationTableCell | null | undefined,
) => {
  const normalized = normalizeTableCell(cell);
  const textNode = normalized.href
    ? (
      <a
        href={normalized.href}
        title={normalized.title ?? undefined}
        target={normalized.external ? '_blank' : undefined}
        rel={normalized.external ? 'noreferrer noopener' : undefined}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          color: '#2697A2',
          fontWeight: 700,
          textDecoration: 'none',
        }}
      >
        {normalized.text}
      </a>
    )
    : normalized.text;

  if (!normalized.detail) {
    return textNode;
  }

  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <span>{textNode}</span>
      <span style={{ color: '#6B7280', fontSize: '10px', lineHeight: 1.35 }}>
        {normalized.detail}
      </span>
    </div>
  );
};

export function PrototypeMinimalPresentationRenderer({
  presentation,
}: {
  presentation: AssistantPresentationModel;
}) {
  const summary = (
    findSection(presentation, 'summary')
    ?? findSection(presentation, 'formatted_body')
  ) as AssistantPresentationSummarySection | AssistantPresentationFormattedBodySection | null;
  const table = findSection(presentation, 'table') as AssistantPresentationTableSection | null;
  const chart = findSection(presentation, 'chart') as AssistantPresentationChartSection | null;
  const metrics = findSection(presentation, 'metric_grid') as AssistantPresentationMetricGridSection | null;
  const confirmation = findSection(presentation, 'confirmation') as AssistantPresentationConfirmationSection | null;
  const chartMaxValue = Math.max(chart?.maxValue ?? 0, ...(chart?.items.map((item) => item.value) ?? []), 1);
  const metricBaseline = metrics?.items.map((item) => parseMetricValue(item.value)).find((value): value is number => value != null) ?? null;
  const metricColumnCount = metrics?.items.length === 4 ? 2 : Math.min(Math.max(metrics?.items.length ?? 1, 1), 3);

  return (
    <div style={{ display: 'grid', gap: 6, width: '100%' }}>
      {summary ? (
        <div
          data-testid="ai-message-content-assistant"
          className={styles.messageContent}
          style={{
            padding: '10px 14px',
            background: '#F4F6F3',
            color: '#404F4D',
            borderRadius: '12px 12px 12px 4px',
          }}
        >
          {renderSummaryText(summary.text)}
        </div>
      ) : null}

      {table ? (
        <section
          data-testid="ai-table-section"
          style={{
            overflow: 'hidden',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            background: '#FFFFFF',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ background: '#F4F6F3' }}>
                {table.columns.map((column) => (
                  <th
                    key={column.id}
                    style={{
                      padding: '6px 10px',
                      textAlign: column.align === 'right' ? 'right' : 'left',
                      fontFamily: 'Mulish',
                      fontSize: '11px',
                      fontWeight: 600,
                      color: '#404F4D',
                    }}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, rowIndex) => (
                <tr
                  key={row.id ?? `row-${rowIndex + 1}`}
                  style={{
                    borderTop: '1px solid #E5E7EB',
                    background: rowIndex % 2 === 1 ? '#F8FAF9' : '#FFFFFF',
                  }}
                >
                  {row.cells.map((cell, cellIndex) => (
                    <td
                      key={`${row.id ?? rowIndex}-${cellIndex + 1}`}
                      style={{
                        padding: '6px 10px',
                        textAlign: table.columns[cellIndex]?.align === 'right' ? 'right' : 'left',
                        fontSize: '11px',
                        lineHeight: 1.45,
                        color: '#404F4D',
                      }}
                    >
                      {renderTableCellContent(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {table.footerText ? (
            <div
              style={{
                padding: '6px 10px',
                borderTop: '1px solid #E5E7EB',
                color: '#6B7280',
                fontSize: '11px',
              }}
            >
              {renderFooterText(table.footerText)}
            </div>
          ) : null}
        </section>
      ) : null}

      {chart ? (
        <section
          data-testid="ai-chart-section"
          style={{
            padding: '9px 10px 8px',
            border: '1px solid #E5E7EB',
            borderRadius: 8,
            background: '#FFFFFF',
          }}
        >
          <div
            style={{
              marginBottom: 4,
              color: '#404F4D',
              fontFamily: 'Mulish',
              fontSize: '11px',
              fontWeight: 700,
            }}
          >
            {chart.chartTitle || chart.title}
          </div>
          <div style={{ maxWidth: 400, aspectRatio: '260 / 100' }}>
            <svg viewBox="0 0 260 100" style={{ width: '100%', height: '100%' }}>
              {chart.items.map((item, index) => {
                const x = 30 + (index * 48);
                const height = Math.max(14, Math.round((item.value / chartMaxValue) * 54));
                return (
                  <g key={item.id}>
                    <rect
                      x={x}
                      y={85 - height}
                      width={28}
                      height={height}
                      rx={3}
                      fill={item.tone === 'accent' ? '#08BFAE' : '#2697A2'}
                      opacity={0.85}
                    />
                    <text
                      x={x + 14}
                      y={97}
                      textAnchor="middle"
                      fill="#9CA3AF"
                      style={{ fontSize: '8px', fontFamily: 'Mulish', fontWeight: 700 }}
                    >
                      {item.label}
                    </text>
                  </g>
                );
              })}
              <line x1="20" y1="85" x2="258" y2="85" stroke="#E5E7EB" strokeWidth="0.5" />
            </svg>
          </div>
        </section>
      ) : null}

      {metrics ? (
        <div
          data-testid="ai-metric-grid-section"
          data-ai-metric-count={metrics.items.length}
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${metricColumnCount}, minmax(0, 1fr))`,
            alignItems: 'stretch',
            justifyContent: 'center',
            gap: metrics.items.length === 4 ? 8 : 6,
          }}
        >
          {metrics.items.map((item, index) => {
            const numericValue = parseMetricValue(item.value);
            const progress = metricBaseline && numericValue != null
              ? Math.max(0, Math.min(100, Math.round((numericValue / metricBaseline) * 100)))
              : 100;
            return (
              <div
                key={item.id}
                data-testid={`ai-metric-card-${index + 1}`}
                style={{
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: metrics.items.length === 4 ? 74 : undefined,
                  padding: '8px 10px',
                  borderRadius: 12,
                  border: '1px solid #E5E7EB',
                  background: '#F4F6F3',
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    color: '#6B7280',
                    fontSize: '9px',
                    lineHeight: 1.2,
                    letterSpacing: '0.05em',
                    overflowWrap: 'anywhere',
                    hyphens: 'none',
                  }}
                >
                  {item.label.toUpperCase()}
                </div>
                <div
                  style={{
                    marginTop: 5,
                    color: '#2697A2',
                    fontFamily: 'Mulish',
                    fontSize: '20px',
                    fontWeight: 600,
                    lineHeight: 1,
                    overflowWrap: 'anywhere',
                  }}
                >
                  {item.value}
                </div>
                <div
                  style={{
                    width: `${Math.max(16, Math.round(progress * 0.55))}%`,
                    height: 1,
                    marginTop: 5,
                    borderRadius: 999,
                    background: 'rgba(38,151,162,0.24)',
                  }}
                />
              </div>
            );
          })}
        </div>
      ) : null}

      {confirmation ? (
        <section
          data-testid="ai-confirmation-section"
          style={{
            padding: '9px 10px 9px 12px',
            borderLeft: '4px solid #28E2A7',
            borderRadius: '0 12px 12px 0',
            background: 'rgba(40, 226, 167, 0.06)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <CircleCheckBig size={16} style={{ color: '#28E2A7', marginTop: 2, flexShrink: 0 }} />
            <div>
              <div style={{ color: '#404F4D', fontFamily: 'Mulish', fontSize: '12px', fontWeight: 700 }}>
                {confirmation.titleText}
              </div>
              <div style={{ marginTop: 2, color: '#6B7280', fontSize: '11px' }}>{confirmation.summary}</div>
              {confirmation.meta ? (
                <div style={{ marginTop: 2, color: '#9CA3AF', fontSize: '10px' }}>{confirmation.meta}</div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      <div className={styles.responseActions} data-testid="ai-assistant-response-actions" style={{ gap: 4, marginTop: 2 }}>
        {[ThumbsUp, Download, Printer].map((Icon, index) => (
          <button
            key={index}
            type="button"
            style={{
              width: 24,
              height: 24,
              border: '0',
              borderRadius: 6,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9CA3AF',
              background: 'transparent',
              padding: 0,
            }}
            aria-hidden="true"
            tabIndex={-1}
          >
            <Icon size={14} />
          </button>
        ))}
      </div>
    </div>
  );
}
